import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useResetUserPasswordMutation } from "../services/auth/authApi";
import { formatDisplayUserId } from "../utils/userIdFormat";
import { getDomainAppUrl, getSiteName } from "../utils/domainConfig";

// Close Icon
const CloseIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.05-.06-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z"></path>
  </svg>
);

const PasswordModal = ({ isOpen, onClose, user }) => {
  const [password, setPassword] = useState('');
  const [resetUserPassword, { isLoading }] = useResetUserPasswordMutation();

  useEffect(function() {
    if (isOpen) {
      setPassword('');
    }
  }, [isOpen]);

  const sendWhatsAppMessage = function() {
    if (!user) return;

    const SUBDOMAIN_MAP = {
      client:     '',
      agent:      'admin',
      super:      'admin',
      master:     'admin',
      miniadmin:  'admin',
      superadmin: 'admin',
      admin:      'admin',
    };
    const rawUrl = user.appUrl?.url || null;
    const baseDomain = getDomainAppUrl();
    const clientDomain = `https://${baseDomain}`;
    const userTypeKey = String(user.userType || '').toLowerCase();
    const sub = SUBDOMAIN_MAP[userTypeKey];
    const domain = rawUrl
      ? (rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`)
      : sub === undefined
        ? clientDomain
        : sub === ''
          ? clientDomain
          : `https://${sub}.${baseDomain}`;

    const username = user.username || user.userName || user.userId || '';
    const password = user.plainPassword || '***';

    const message =
      `🏏 *Welcome to ${getSiteName()}!* 🏏\n` +
      `${'━'.repeat(28)}\n\n` +
      `🆔 *Username :* ${username}\n` +
      `🔑 *Password :* ${password}\n` +
      `🌐 *Website  :* ${domain}\n\n` +
      `${'━'.repeat(28)}\n` +
      `⚠️ _Keep your credentials safe._\n` +
      `_Do not share with anyone._`;

    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = user.phone || user.phoneNumber || user.mobile || user.mobileNumber;

    let whatsappUrl;
    if (phoneNumber) {
      const cleanPhone = phoneNumber.toString().replace(/\D/g, '');
      whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
    } else {
      whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    }

    window.open(whatsappUrl, '_blank');
    handleClose();
  };

  const handleSubmit = async function() {
    if (!password.trim()) {
      toast.error('Please enter a password');
      return;
    }

    try {
      const userId = user.id || user._id;
      await resetUserPassword({ userId, newPassword: password }).unwrap();
    //   toast.success('Password reset successfully!');
      handleClose();
    } catch (err) {
    //   toast.error(err?.data?.message || 'Failed to reset password');
    }
  };

  const handleClose = function() {
    setPassword('');
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose}></div>
      <div 
        className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        style={{ boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)' }}
      >
        {/* Close Button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <CloseIcon />
        </button>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Reset Password</h3>
          <p className="text-sm text-gray-500 mt-1">
            User ID: {user?.userId}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Password
          </label>
          <input
            type="text"
            value={password}
            onChange={function(e) { setPassword(e.target.value); }}
            className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:border-blue-500"
            placeholder="Enter new password"
            disabled={isLoading}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button 
            onClick={handleClose}
            className="px-4 py-2 bg-gray-500 text-white hover:bg-gray-600 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={sendWhatsAppMessage}
            className="px-4 py-2 bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            Send via WhatsApp
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-blue-300 transition-colors"
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordModal;
